import { adminApi } from './adminApi';

/** A user's Insight id fields and the Insight list each one comes from (as the admin API's). */
export const USER_INSIGHT_FIELD_TYPES = {
	salesTeamId: 'SalesTeam',
	serviceTeamId: 'ServiceTeam',
	accountTypeId: 'ClientAccountType',
	authorisedRepId: 'AuthorisedRep',
};

/**
 * One Insight list as the user checks take it, from a listInsightFields query's { data,
 * isError, error }: { options, isError, isNotAvailable }. isNotAvailable when the ledger's
 * Insight doesn't offer the list (the API's 501 { notAvailable }), which counts as nothing to
 * pick; any other error is a list that couldn't be read.
 */
export const toInsightList = ({ data, isError, error }) => ({
	options: data ?? [],
	isError,
	isNotAvailable: Boolean(error?.data?.notAvailable),
});

/**
 * Insight lists for a ledger, read by the admin API with that ledger's credentials (POST
 * /insight/fields/list, one Insight GetDropDown call): [{ id, name, isInactive }] sorted by
 * name. fieldType is one of USER_INSIGHT_FIELD_TYPES' values. Cached per ledger and type for
 * the session, as the lists rarely change, so a ledger costs one call per list.
 */
export const adminInsightFieldsApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		listInsightFields: builder.query({
			query: ({ ledger, fieldType }) => ({
				url: '/insight/fields/list',
				method: 'POST',
				body: { ledger, fieldType },
			}),
			keepUnusedDataFor: 60 * 60,
		}),
		/**
		 * Every user Insight list (USER_INSIGHT_FIELD_TYPES) of each of `ledgers` (sorted, so
		 * the same ledgers share a cache entry), through listInsightFields, so a list the
		 * pickers already loaded isn't read again. Responds { `${ledger}:${fieldType}`: {
		 * options, isError, isNotAvailable } } (toInsightList); a list that can't be read is
		 * isError rather than failing the rest.
		 */
		listInsightFieldsForLedgers: builder.query({
			queryFn: async (ledgers, { dispatch }) => {
				const lists = await Promise.all(
					ledgers.flatMap((ledger) =>
						Object.values(USER_INSIGHT_FIELD_TYPES).map(async (fieldType) => {
							const { data, isError, error } = await dispatch(
								adminInsightFieldsApi.endpoints.listInsightFields.initiate(
									{ ledger, fieldType },
									{ subscribe: false },
								),
							);
							return [
								`${ledger}:${fieldType}`,
								toInsightList({ data, isError, error }),
							];
						}),
					),
				);
				return { data: Object.fromEntries(lists) };
			},
			keepUnusedDataFor: 60 * 60,
		}),
	}),
});

export const { useListInsightFieldsQuery, useListInsightFieldsForLedgersQuery } =
	adminInsightFieldsApi;
